import React, { useState, useEffect } from "react";
import Modal from "components/modal";

const SosialyFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialSosialy, 
  isEditing,
  membres = [],
  membresLoading = false,
  moisOptions = [],
  loading = false
}) => {
  const [sosialy, setSosialy] = useState({
    id_membre: "",
    mois_sosialy: "",
    annee_sosialy: new Date().getFullYear().toString(),
    montant_sosialy: "",
    date_sosialy: new Date().toISOString().split('T')[0],
    objet_sosialy: "Cotisation mensuelle"
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Générer une liste d'années (de l'année actuelle - 5 à l'année actuelle + 5)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 5; year <= currentYear + 5; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const years = generateYears();

  useEffect(() => {
    if (isOpen && initialSosialy) {
      setSosialy({
        id_membre: initialSosialy.id_membre || "",
        mois_sosialy: initialSosialy.mois_sosialy || "",
        annee_sosialy: initialSosialy.annee_sosialy || new Date().getFullYear().toString(),
        montant_sosialy: initialSosialy.montant_sosialy || "",
        date_sosialy: initialSosialy.date_sosialy || new Date().toISOString().split('T')[0],
        objet_sosialy: initialSosialy.objet_sosialy || "Cotisation mensuelle"
      });
      setValidationErrors({});
    }
  }, [isOpen, initialSosialy]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "montant_sosialy") {
      // Ne permettre que des nombres et un seul point décimal
      const regex = /^[0-9]*\.?[0-9]*$/;
      if (value === '' || regex.test(value)) {
        setSosialy({ ...sosialy, [name]: value });
      }
    } else {
      setSosialy({ ...sosialy, [name]: value });
    }

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!sosialy.id_membre) {
      errors.id_membre = "Le membre est requis";
    }

    if (!sosialy.mois_sosialy) {
      errors.mois_sosialy = "Le mois est requis";
    }

    if (!sosialy.annee_sosialy) {
      errors.annee_sosialy = "L'année est requise";
    }

    if (!sosialy.montant_sosialy || parseFloat(sosialy.montant_sosialy) <= 0) {
      errors.montant_sosialy = "Le montant doit être supérieur à 0";
    }

    if (!sosialy.date_sosialy) {
      errors.date_sosialy = "La date est requise";
    }

    if (!sosialy.objet_sosialy.trim()) {
      errors.objet_sosialy = "L'objet de la cotisation est requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      id_membre: sosialy.id_membre,
      mois_sosialy: sosialy.mois_sosialy,
      annee_sosialy: sosialy.annee_sosialy,
      montant_sosialy: sosialy.montant_sosialy,
      date_sosialy: sosialy.date_sosialy,
      objet_sosialy: sosialy.objet_sosialy,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier la cotisation"
              : "Enregistrer une nouvelle cotisation"}
          </h3>
          <div className="h-1 w-12 bg-green-600 mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="id_membre"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Membre *
            </label>
            <select
              id="id_membre"
              name="id_membre"
              value={sosialy.id_membre}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-green-500 focus:outline-none focus:ring-green-500"
              disabled={membresLoading}
            >
              <option value="">Sélectionner un membre</option>
              {membresLoading ? (
                <option value="" disabled>Chargement des membres...</option>
              ) : (
                membres.map(membre => (
                  <option key={membre.id_membre} value={membre.id_membre}>
                    {membre.nom_prenom_membre} {membre.com_origin ? `(${membre.com_origin})` : ''}
                  </option>
                ))
              )}
            </select>
            {validationErrors.id_membre && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.id_membre}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="mois_sosialy"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mois *
              </label>
              <select
                id="mois_sosialy"
                name="mois_sosialy"
                value={sosialy.mois_sosialy}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-green-500 focus:outline-none focus:ring-green-500"
              >
                <option value="">Sélectionner un mois</option>
                {moisOptions.map(mois => (
                  <option key={mois} value={mois}>{mois}</option>
                ))}
              </select>
              {validationErrors.mois_sosialy && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.mois_sosialy}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="annee_sosialy"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Année *
              </label>
              <select
                id="annee_sosialy"
                name="annee_sosialy"
                value={sosialy.annee_sosialy}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-green-500 focus:outline-none focus:ring-green-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {validationErrors.annee_sosialy && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.annee_sosialy}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="montant_sosialy"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Montant (MGA) *
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="montant_sosialy"
                  id="montant_sosialy"
                  value={sosialy.montant_sosialy}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 pr-12 focus:border-green-500 focus:outline-none focus:ring-green-500"
                  placeholder="Ex: 5000"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm">Ar</span>
                </div>
              </div>
              {validationErrors.montant_sosialy && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.montant_sosialy}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="date_sosialy"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date de paiement *
              </label>
              <input
                type="date"
                name="date_sosialy"
                id="date_sosialy"
                value={sosialy.date_sosialy}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-green-500 focus:outline-none focus:ring-green-500"
              />
              {validationErrors.date_sosialy && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.date_sosialy}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="objet_sosialy"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Objet de la cotisation *
            </label>
            <input
              type="text"
              name="objet_sosialy"
              id="objet_sosialy"
              value={sosialy.objet_sosialy}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-green-500 focus:outline-none focus:ring-green-500"
              placeholder="Ex: Cotisation mensuelle"
            />
            {validationErrors.objet_sosialy && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.objet_sosialy}
              </p>
            )}
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SosialyFormModal;